/**
 * Dracula — „Cobble & Fog".
 *
 * BLOODTHIRSTY: na začiatku ťahu smie Dracula dať 1 poškodenie susednej
 * figúrke (akejkoľvek) — ak to urobí, potiahne kartu. Sestry (The Sisters)
 * majú po 1 zdraví a karty ich vracajú do hry.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardFromHand, drawCard, gainAction, getMap, healFighter } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { draw, feint } from './common.js';
import { askMove, fighterOrNull } from './helpers.js';
import { doMyBidding } from './philippa.js';
import { ambush } from './robinHood.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const DRACULA = 'dracula';
const SISTER = 'the-sisters';
const BEASTFORM = 'dracula.beastform';
const RAVENING = 'dracula.ravening-seduction';

const draculaOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === DRACULA) ?? null;

const sistersOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.filter((f) => f.owner === player && f.defId === SISTER);

const emptyAround = (ctx: Ctx, spaceId: SpaceId) =>
  neighbors(getMap(ctx), spaceId).filter((id) => !ctx.state.fighters.some((f) => f.spaceId === id));

/** „Move your fighter up to 3 spaces." */
const dash: CardEffect = {
  after: (e) => {
    if (e.fighterId) askMove(e, { fighters: [e.fighterId], max: 3, label: 'Dash: presuň figúrku až o 3' });
  },
};

/** „This card's value is +1 for each Sister in the same zone as the opposing fighter." */
const feedingFrenzy: CardEffect = {
  during: (e, value) => {
    const target = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!target) return value;
    const map = getMap(e.ctx);
    return value + sistersOf(e.ctx, e.player).filter((s) => shareZone(map, s.spaceId, target.spaceId)).length;
  },
};

/** „You may discard any number of cards from your hand. This card's value is +1 for each card you discard." */
function askBeast(ctx: Ctx, player: PlayerIndex, source: string, side: 'attacker' | 'defender'): void {
  if ((ctx.state.players[player]?.hand.length ?? 0) === 0) return;
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: 'Beastform: odhoď kartu za +1 (koľko chceš)',
    optional: true,
    from: 'hand',
    purpose: 'effect',
    discards: true,
    side,
  });
}

const beastform: CardEffect = {
  duringChoice: (e) => askBeast(e.ctx, e.player, e.cardId, e.role === 'defender' ? 'defender' : 'attacker'),
  onCardChosen: (ctx, choice, cardId) => {
    const combat = ctx.state.combat;
    if (!combat || !choice.source) return;
    discardFromHand(ctx, choice.player, cardId, 'effect');
    const side = choice.side ?? 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 1;
    askBeast(ctx, choice.player, choice.source, side);
  },
};

/** „Add the BOOST value from your opponent's attack card to the defense value of this card." */
const lookIntoMyEyes: CardEffect = {
  during: (e, value) => {
    const combat = e.ctx.state.combat;
    if (e.role !== 'defender' || !combat?.attackCard || combat.bonus) return value;
    return value + cardDef(e.ctx, combat.attackCard).boost;
  },
};

/** „Place Dracula in any space. Gain 1 action." */
const mistform: CardEffect = {
  scheme: (e) => {
    const dracula = draculaOf(e.ctx, e.player);
    if (dracula) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: 'Mistform: polož Draculu na ľubovoľný priestor',
        optional: false,
        fighterId: dracula.id,
        spaces: getMap(e.ctx).spaces.map((s) => s.id).filter((id) => !e.ctx.state.fighters.some((f) => f.spaceId === id)),
      });
    }
    gainAction(e.ctx, e.player);
  },
};

/** „Deal 1 damage to all opposing fighters adjacent to Dracula. Dracula recovers 1 health for each damage dealt." */
const preyUpon: CardEffect = {
  scheme: (e) => {
    const dracula = draculaOf(e.ctx, e.player);
    if (!dracula) return;
    const around = new Set(neighbors(getMap(e.ctx), dracula.spaceId));
    const targets = e.ctx.state.fighters.filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && around.has(f.spaceId));
    let dealt = 0;
    for (const target of targets) {
      const before = target.hp;
      dealDamage(e.ctx, target.id, 1);
      const after = fighterOrNull(e.ctx, target.id)?.hp ?? 0;
      dealt += before - after;
    }
    healFighter(e.ctx, dracula.id, dealt);
  },
};

/** „Recover 2 health. Return a defeated Sister (if any) to any space in Dracula's zone." */
const baptismOfBlood: CardEffect = {
  scheme: (e) => {
    const dracula = draculaOf(e.ctx, e.player);
    if (!dracula) return;
    healFighter(e.ctx, dracula.id, 2);
    const fallen = (e.ctx.state.defeated ?? []).find((f) => f.owner === e.player && f.defId === SISTER);
    if (!fallen) return;
    const map = getMap(e.ctx);
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Baptism of Blood: vráť porazenú Sestru do Draculovej zóny',
      optional: false,
      fighterId: fallen.id,
      spaces: map.spaces
        .map((s) => s.id)
        .filter((id) => shareZone(map, id, dracula.spaceId) && !e.ctx.state.fighters.some((f) => f.spaceId === id)),
    });
  },
};

/** „If you won the combat, place Dracula in any space adjacent to the opposing fighter." */
const thirstForSustenance: CardEffect = {
  after: (e) => {
    const dracula = draculaOf(e.ctx, e.player);
    const target = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!e.won || !dracula || !target) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Thirst for Sustenance: polož Draculu vedľa súperovej figúrky',
      optional: false,
      fighterId: dracula.id,
      spaces: emptyAround(e.ctx, target.spaceId),
    });
  },
};

/**
 * „Move any fighter up to 2 spaces. After moving, deal 1 damage to the moved
 * fighter for each Sister adjacent to them." Presun vyberá hráč karty.
 */
const raveningSeduction: CardEffect = {
  scheme: (e) => {
    askMove(e, {
      fighters: e.ctx.state.fighters.map((f) => f.id),
      max: 2,
      label: 'Ravening Seduction: presuň ľubovoľnú figúrku až o 2',
      follow: { kind: 'effect', effect: RAVENING, data: 'bite' },
    });
  },
  onFollow: (ctx, choice, _data, context) => {
    const moved = fighterOrNull(ctx, context?.fighterId ?? null);
    if (!moved) return;
    const around = new Set(neighbors(getMap(ctx), moved.spaceId));
    const bites = sistersOf(ctx, choice.player).filter((s) => s.id !== moved.id && around.has(s.spaceId)).length;
    dealDamage(ctx, moved.id, bites);
  },
};

export const draculaCards: EffectRegistry = {
  'dracula.feint': feint,
  'dracula.dash': dash,
  'dracula.exploit': draw(1),
  'dracula.ambush': ambush,
  'dracula.feeding-frenzy': feedingFrenzy,
  [BEASTFORM]: beastform,
  'dracula.do-my-bidding': doMyBidding,
  'dracula.look-into-my-eyes': lookIntoMyEyes,
  'dracula.mistform': mistform,
  'dracula.prey-upon': preyUpon,
  'dracula.baptism-of-blood': baptismOfBlood,
  'dracula.thirst-for-sustenance': thirstForSustenance,
  [RAVENING]: raveningSeduction,
};

/** BLOODTHIRSTY — „At the start of your turn, you may deal 1 damage to a fighter adjacent to Dracula. If you do, draw a card." */
export const draculaSpecial: HeroSpecial = {
  name: 'Bloodthirsty',
  startOfTurn: (ctx, player) => {
    const dracula = draculaOf(ctx, player);
    if (!dracula) return;
    const around = new Set(neighbors(getMap(ctx), dracula.spaceId));
    const targets = ctx.state.fighters.filter((f) => around.has(f.spaceId));
    if (targets.length === 0) return;
    askChoice(ctx, {
      kind: 'option',
      player,
      source: null,
      label: 'Bloodthirsty: smieš dať 1 poškodenie susednej figúrke a potiahnuť kartu',
      optional: true,
      options: targets.map((f) => ({
        label: `${f.name}${f.owner === player ? ' (vlastná)' : ''}`,
        follow: { kind: 'special' as const, hero: DRACULA, data: f.id },
      })),
    });
  },
  onFollow: (ctx, player, targetId) => {
    if (!fighterOrNull(ctx, targetId)) return;
    dealDamage(ctx, targetId, 1);
    drawCard(ctx, player);
  },
};
